import { Component, EventEmitter, HostListener, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { UtilService } from 'app/shared/util.service';
import { TranslateService } from '@ngx-translate/core';
import { DefaultLanguageState } from 'app/states/default-language/default-language.state';
import { Select } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { SelectorStateService } from './selector-state.service';

interface TrendOption {
  name: string | number;
  original: string | number;
  value: string | number;
  action?: string | number;
}

@Component({
    selector: 'mh-index-selector',
    templateUrl: './index-selector.component.html',
    standalone: false
})
export class IndexSelectorComponent implements OnInit, OnDestroy {
  @Select(DefaultLanguageState.languageCode) language$: Observable<string>;

  @Input() actions: { [key: string | number]: string | number } = {
    nps: 'getHistoricNPS',
    ranking_areas: 'getHistoricArea',
    ranking: 'getTripAdvisor',
    pms: 'getHistoricPMS',
    sent: 'getEmailsArea',
    answered: 'getEmailsArea',
    rate: 'getEmailsArea',
    email_surveys: 'getEmailsArea',
    open_surveys: 'getEmailsArea',
    total_guests: 'getHistoricPMS',
    promoters: 'getHistoricNPS',
    passives: 'getHistoricNPS',
    detractors: 'getHistoricNPS',
    survey_total_score: 'getScore',
    semantic_index: 'getSemanticIndex',
  };

  @Input() selectedOption = 'nps';
  @Input() translationPrefix: string = 'widgets.trends.filters';
  @Input() valueType: 'string' | 'number' = 'string';
  @Input() optionsWithTag: string[] = [];

  @Output() optionSelected = new EventEmitter<TrendOption>();

  options: any[] = [];
  isOpen = false;
  currentTrend: TrendOption;
  subscriptions = new Subscription();
  private selectorId: string = 'selector_' + Math.random().toString(36).substring(2, 9);

  constructor(
    private utilService: UtilService,
    private translateService: TranslateService,
    private selectorStateService: SelectorStateService
  ) {}

  ngOnInit() {
    this.buildOptions();
    this.setInitialTrend();
    this.subscriptions.add(this.language$.subscribe((language) => {
      this.buildOptions();
      this.setInitialTrend();
    }));
    
    // Register this selector with the service
    this.selectorStateService.register(this.selectorId, (isOpen) => {
      this.isOpen = isOpen;
    });
  }

  buildOptions() {
    this.options = Object.keys(this.actions).map((key) => {
      return {
        id: this.valueType === 'string' ? key : +key,
        label: this.translationPrefix + '.' + key,
        action: this.actions[key],
        hasNewTag: this.optionsWithTag.includes(key),
      };
    });
  }

  setInitialTrend() {
    this.currentTrend = {
      name: this.translationPrefix + '.' + this.selectedOption,
      original: this.selectedOption,
      value: this.selectedOption,
      action: this.getActionForOption(this.selectedOption)
    };
  }

  @HostListener('document:click', ['$event'])
  onClick(event) {
    const result = this.checkClickInside(event.target);
    if (!result) {
      this.isOpen = false;
      if (this.selectorStateService.isCurrentOpenSelector(this.selectorId)) {
        this.selectorStateService.setOpenSelector(null);
      }
    }
  }

  toggleOpen() {
    if (!this.isOpen) {
      // Close any other open selector before opening this one
      this.selectorStateService.setOpenSelector(this.selectorId);
      this.isOpen = true;
    } else {
      this.isOpen = false;
      this.selectorStateService.setOpenSelector(null);
    }
  }

  selectOption(optionId: string) {
    this.selectedOption = optionId;
    this.currentTrend = {
      name: this.translationPrefix + '.' + optionId,
      original: optionId,
      value: optionId,
      action: this.getActionForOption(optionId)
    };
    this.optionSelected.emit(this.currentTrend);
    this.isOpen = false;
    this.selectorStateService.setOpenSelector(null);
  }

  checkClickInside(target) {
    if (target.id === 'indexSelectorTrigger' || 
        target.id === 'indexSelectorMenu' || 
        target.className?.includes('mat-radio') ||
        this.isChildOfElementWithId(target, 'indexSelectorTrigger') ||
        this.isChildOfElementWithId(target, 'indexSelectorMenu')) {
      return true;
    } else if (target.parentNode) {
      return this.checkClickInside(target.parentNode);
    } else {
      return false;
    }
  }

  isChildOfElementWithId(element, id) {
    let parent = element.parentNode;
    while (parent) {
      if (parent.id === id) {
        return true;
      }
      parent = parent.parentNode;
    }
    return false;
  }

  get language() { 
    return this.utilService.getCurrentLanguage(); 
  }

  get selectedOptionLabel(): string {
    const option = this.options.find(opt => opt.id === this.selectedOption);
    return option ? option.label : this.translationPrefix + '.nps';
  }

  getActionForOption(optionId: string) {
    return this.actions[optionId] || '';
  }

  ngOnDestroy() {
    this.selectorStateService.unregister(this.selectorId);
    if (this.selectorStateService.isCurrentOpenSelector(this.selectorId)) {
      this.selectorStateService.setOpenSelector(null);
    }
    this.subscriptions.unsubscribe();
  }
}
